import { EntityType } from "@mapmycustomers/shared/enum";
import { RouteType } from "@mapmycustomers/shared/enum/routes/RouteType";
import Route, {
  CreateRoutePayload,
  CreateRouteStopItem,
  PreviewRoutePayload,
  RouteEndLocation,
  RouteStartLocation,
  RouteStop,
} from "@mapmycustomers/shared/types/entity/Route";
import RouteDetail from "@mapmycustomers/shared/types/entity/RouteDetail";

import getRemainingRouteStops from "@app/util/route/getRemainingRouteStops";
import displayOrderComparator from "@app/util/sorting/displayOrderComparator";

const toCreateRouteStopItem = (stop: RouteStop): CreateRouteStopItem => {
  if (stop.entity === EntityType.COMPANY) {
    return {
      accountId: stop.accountId,
      allottedTime: stop.allottedTime,
      displayOrder: stop.displayOrder,
      scheduledTime: stop.scheduledTime ?? null,
      syncedActivityId: stop.syncedActivityId ?? null,
    };
  }
  if (stop.entity === EntityType.PERSON) {
    return {
      allottedTime: stop.allottedTime,
      contactId: stop.contactId,
      displayOrder: stop.displayOrder,
      scheduledTime: stop.scheduledTime ?? null,
      syncedActivityId: stop.syncedActivityId ?? null,
    };
  }
  return {
    allottedTime: stop.allottedTime,
    displayOrder: stop.displayOrder,
    manualAddress: stop.manualAddress,
    manualGeoPoint: stop.manualGeoPoint,
    manualName: stop.manualName,
    scheduledTime: stop.scheduledTime ?? null,
    syncedActivityId: stop.syncedActivityId ?? null,
  };
};

const pickStartLocation = (routeDetail: RouteDetail): RouteStartLocation => {
  if (routeDetail.startEntity) {
    return { startEntity: routeDetail.startEntity };
  }
  // Manual variant requires both address + geoPoint to be non-null.
  return {
    startAddress: routeDetail.startAddress as string,
    startGeoPoint: routeDetail.startGeoPoint as NonNullable<RouteDetail["startGeoPoint"]>,
  };
};

const pickEndLocation = (routeDetail: RouteDetail): Partial<RouteEndLocation> => {
  if (routeDetail.endEntity) {
    return { endEntity: routeDetail.endEntity };
  }
  if (routeDetail.endAddress != null && routeDetail.endGeoPoint != null) {
    return {
      endAddress: routeDetail.endAddress,
      endGeoPoint: routeDetail.endGeoPoint,
    };
  }
  return {};
};

interface BuildPreviewPayloadOptions {
  // Send every stop, checked-off included, measured in stored order. Used when the route is
  // complete: "remaining" is then empty, and the only drive worth pricing is the whole one.
  allStops?: boolean;
}

/**
 * Past pins and activity links trip add-on gates (v1073/v1074) on any org without
 * `advancedRouting`, 400ing the whole call, and neither affects a totals-only measurement.
 * `syncedActivityId` goes too: the BE re-creates the pin on activity-linked stops that lack one.
 */
export const stripPinFields = ({
  scheduledTime,
  syncedActivityId,
  ...rest
}: CreateRouteStopItem): CreateRouteStopItem => rest;

const buildPreviewPayloadFromRoute = (
  route: Pick<Route, "routeDetail" | "stopTime">,
  options: BuildPreviewPayloadOptions = {}
): PreviewRoutePayload => {
  const routeDetail: CreateRoutePayload["routeDetail"] = {
    startAt: route.routeDetail.startAt,
    travelMode: route.routeDetail.travelMode,
    // Never re-optimize an all-stops solve: the rep has already driven this order, and in the
    // editor a reorder is persisted, which would rewrite the record of what they did.
    type: options.allStops ? RouteType.ORDERED : route.routeDetail.type,
    ...(route.routeDetail.allottedTime != null
      ? { allottedTime: route.routeDetail.allottedTime }
      : {}),
    ...pickStartLocation(route.routeDetail),
    ...pickEndLocation(route.routeDetail),
  };

  const stops = options.allStops
    ? (route.stopTime ?? []).toSorted(displayOrderComparator)
    : getRemainingRouteStops(route);
  const stopTime = stops.map(toCreateRouteStopItem);

  return {
    routeDetail,
    stopTime: options.allStops ? stopTime.map(stripPinFields) : stopTime,
  };
};

export default buildPreviewPayloadFromRoute;
